import React, { useState, useEffect } from 'react'
import { FaMinus } from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import eth_yellow from "../Assets/eth_yellow.png";
import { useWallet } from '../../contexts/WalletContext.js';
import { ethers } from 'ethers';
import ErrorModal from '../ErrorModal/ErrorModal.jsx';

export default function NFT_video({video_url, nft_name, price = "$150", minted = "0/1000", minting_price = "$150"}) {
  const { account, provider, signer, isConnected, CONTRACT_ADDRESS, CONTRACT_ABI } = useWallet();
  const [mintAmount, setMintAmount] = useState(1);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [minting, setMinting] = useState(false);
  const [ethPrice, setEthPrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  });

  // Extract token ID from nft_name (e.g., "WING P #1" -> 1)
  const tokenId = parseInt(nft_name.split('#')[1]);

  // Helper function to show modal
  const showModal = (title, message, type = 'error') => {
    setModal({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    // Always load token info for price display
    if (tokenId) {
      if (provider && isConnected) {
        // Use connected wallet provider if available
        loadTokenInfo();
      } else {
        // Use read-only provider for price fetching (no wallet needed)
        loadTokenInfoReadOnly();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, provider, tokenId]);

  const loadTokenInfo = async () => {
    try {
      setLoading(true);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, provider);
      
      // Get token info
      const tokenData = await contract.getTokenInfo(tokenId);
      const mintPriceWei = await contract.mintPrice(tokenId);
      const mintPriceEth = parseFloat(ethers.utils.formatEther(mintPriceWei));
      
      // Get ETH price
      const ethPriceWei = await contract.getLatestPriceETH();
      const ethPriceUsd = parseFloat(ethers.utils.formatEther(ethPriceWei));
      setEthPrice(ethPriceUsd);

      setTokenInfo({
        exists: tokenData.tokenExists,
        maxSupply: tokenData.maxSupplyAmount.toString(),
        currentSupply: tokenData.currentSupplyAmount.toString(),
        price: mintPriceEth,
        priceWei: mintPriceWei,
        uri: tokenData.tokenURI
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading token info:', error);
      setLoading(false);
    }
  };

  const loadTokenInfoReadOnly = async () => {
    try {
      setLoading(true);
      // Try multiple RPC endpoints for better reliability
      const rpcEndpoints = [
        'https://eth.llamarpc.com',
        'https://ethereum.publicnode.com',
        'https://rpc.ankr.com/eth'
      ];
      
      let readOnlyProvider = null;
      for (const endpoint of rpcEndpoints) {
        try {
          readOnlyProvider = new ethers.providers.JsonRpcProvider(endpoint);
          // Test the connection
          await readOnlyProvider.getNetwork();
          break;
        } catch (e) {
          console.log(`RPC endpoint ${endpoint} failed, trying next...`);
          continue;
        }
      }
      
      if (!readOnlyProvider) {
        throw new Error('All RPC endpoints failed');
      }
      
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, readOnlyProvider);
      
      // Get token info
      const tokenData = await contract.getTokenInfo(tokenId);
      const mintPriceWei = await contract.mintPrice(tokenId);
      const mintPriceEth = parseFloat(ethers.utils.formatEther(mintPriceWei));
      
      // Get ETH price
      const ethPriceWei = await contract.getLatestPriceETH();
      const ethPriceUsd = parseFloat(ethers.utils.formatEther(ethPriceWei));
      setEthPrice(ethPriceUsd);

      setTokenInfo({
        exists: tokenData.tokenExists,
        maxSupply: tokenData.maxSupplyAmount.toString(),
        currentSupply: tokenData.currentSupplyAmount.toString(),
        price: mintPriceEth,
        priceWei: mintPriceWei,
        uri: tokenData.tokenURI
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading token info (read-only):', error);
      // Fallback to default values if contract call fails
      setTokenInfo(null);
      setLoading(false);
    }
  };

  const handleMint = async () => {
    if (!isConnected || !signer) {
      showModal(
        'Wallet Not Connected',
        'Please connect your wallet first to mint NFTs.',
        'warning'
      );
      return;
    }

    if (!tokenInfo) {
      showModal(
        'Loading Error',
        'Token information is still loading. Please wait a moment and try again.',
        'info'
      );
      return;
    }

    try {
      setMinting(true);
      
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);
      const totalPrice = tokenInfo.priceWei.mul(mintAmount);
      
      const tx = await contract.mint(tokenId, mintAmount, {
        value: totalPrice
      });

      console.log('Transaction sent:', tx.hash);
      
      const receipt = await tx.wait();
      console.log('Transaction confirmed:', receipt);
      
      showModal(
        'Minting Successful! 🎉',
        `Successfully minted ${mintAmount} token(s) of ${nft_name}! Your NFTs will appear in your wallet shortly.`,
        'success'
      );
      
      // Reload token info
      await loadTokenInfo();
      
    } catch (error) {
      console.error('Error minting token:', error);
      if (error.code === 'INSUFFICIENT_FUNDS') {
        showModal(
          'Insufficient Funds 💰',
          'You don\'t have enough ETH in your wallet to complete this transaction. Please add more ETH and try again.',
          'error'
        );
      } else if (error.code === 'USER_REJECTED') {
        showModal(
          'Transaction Cancelled ❌',
          'You cancelled the transaction. No charges were made to your wallet.',
          'warning'
        );
      } else {
        showModal(
          'Minting Failed ⚠️',
          `Transaction failed: ${error.message || 'Unknown error occurred'}. Please try again.`,
          'error'
        );
      }
    } finally {
      setMinting(false);
    }
  };

  const incrementAmount = () => {
    setMintAmount(prev => Math.min(prev + 1, 10)); // Max 10
  };

  const decrementAmount = () => {
    setMintAmount(prev => Math.max(prev - 1, 1)); // Min 1
  };

  const displayPrice = tokenInfo ? tokenInfo.price : 0;
  const displayMinted = tokenInfo ? `${tokenInfo.currentSupply}/${tokenInfo.maxSupply}` : minted;
  const displayUsdPrice = tokenInfo && ethPrice > 0 ? `$${(displayPrice * ethPrice).toFixed(2)}` : (loading ? 'Loading...' : price);
  const isSoldOut = tokenInfo ? parseInt(tokenInfo.currentSupply) >= parseInt(tokenInfo.maxSupply) : false;
  return (
    <div className="smal_nft">
      <video 
        className="largrknf" 
        controls 
        muted 
        loop 
        autoPlay
        preload="metadata"
        poster="" // You can add a poster image here if needed
      >
        <source src={video_url} type="video/mp4" />
        <source src={video_url} type="video/webm" />
        <source src={video_url} type="video/ogg" />
        Your browser does not support the video tag.
      </video>

      <div className="content_nft">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex gap-2 align-items-center">
            <h6 className="card_head mb-0 robo_font">{nft_name}</h6>
          </div>
          <div className="">
            <p className="mb-0 price_tag robo_font">
              {" "}
              <span className="price_ineth">
                <img src={eth_yellow} className='d-none yelll_eth_he d-md-flex' alt="" /> 
                {loading ? 'Loading...' : tokenInfo ? `${displayPrice.toFixed(6)} ETH` : 'Connect wallet to see price'}
              </span>
            </p>
            <p className="mb-0 price_tag robo_font" style={{fontSize: '0.8rem', color: '#666'}}>
              {displayUsdPrice}
            </p>
          </div>
        </div>
        <p className="minnted  mt-2 mb-0 robo_font">Minted: {displayMinted}</p>
        {isSoldOut && (
          <p className="minnted robo_font" style={{color: '#ff4444', fontWeight: 'bold'}}>
            SOLD OUT
          </p>
        )}
        <div className="row justify-content-between">
          <div className="col-md-6">
            <div className="count_box d-flex gap-2 justify-content-center  align-items-center">
              <span className="count_span" onClick={decrementAmount} style={{cursor: 'pointer'}}>
                {" "}
                <FaMinus />
              </span>
              <h6 className="nft_number mb-0 robo_font">{mintAmount}</h6>
              <span className="count_span" onClick={incrementAmount} style={{cursor: 'pointer'}}>
                {" "}
                <FaPlus />
              </span>
            </div>
          </div>
          <div className="col-md-6 text-end">
            <button 
              className="mint_nft_btn robo_font"
              onClick={handleMint}
              disabled={minting || isSoldOut || !isConnected}
            >
              {minting ? 'Minting...' : isSoldOut ? 'Sold Out' : !isConnected ? 'Connect Wallet' : 'Mint NFT'}
            </button>
          </div>
        </div>
      </div>
      
      <ErrorModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </div>
  );
}
